import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type ClientId } from '../../client/bridge-api-client-types';

import { findTopmostSameOriginWindow, isTop } from './browser-apis';

const MAX_FRAME_TRAVERSAL_DEPTH = 16;

const findIframe = (
	targetDocument: Document | undefined,
	receiverId: ClientId,
): HTMLIFrameElement | undefined => {
	if (fg('rovo_bridge_validate_message_origin')) {
		// Client IDs come from messages and must not be interpreted as selector syntax.
		return Array.from(
			targetDocument?.querySelectorAll<HTMLIFrameElement>('iframe[data-rovo-receiver-id]') ?? [],
		).find((frame) => frame.getAttribute('data-rovo-receiver-id') === receiverId);
	}

	return (
		targetDocument?.querySelector<HTMLIFrameElement>(`[data-rovo-receiver-id="${receiverId}"]`) ??
		undefined
	);
};

const getDocument = (targetWindow: Window): Document | undefined => {
	try {
		return targetWindow.document;
	} catch {
		return undefined;
	}
};

const getFrames = (targetWindow: Window): Window[] => {
	try {
		return Array.from(targetWindow.frames);
	} catch {
		return [];
	}
};

/**
 * Traverses through all child frames (same-origin) which the current window has access to.
 * Required to discover child frames which are deeply nested as transport targets.
 *
 * @param receiverId bridge API client we are attempting to communicate with
 * @param rootWindow the current host / root window we are traversing down
 * @param visited windows we have already searched through
 * @param depth current depth - to enforce a maximum depth in our DFS
 *
 * @returns the relevant window or undefined
 */
const resolveChildIframeFromWindow = (
	receiverId: ClientId,
	rootWindow: Window,
	visited: WeakSet<Window>,
	depth: number,
): HTMLIFrameElement | undefined => {
	// if we have already searched this window, early exit
	if (visited.has(rootWindow)) {
		return undefined;
	}

	// if we can no longer traverse further downwards, early exit
	if (depth > MAX_FRAME_TRAVERSAL_DEPTH) {
		return undefined;
	}

	// mark as visited
	visited.add(rootWindow);

	// get hold of the document & look for the current receiver of interest
	const rootDocument = getDocument(rootWindow);
	const frame = findIframe(rootDocument, receiverId);
	if (frame?.contentWindow) {
		return frame;
	}

	// if not found at the top-level, traverse down
	for (const childWindow of getFrames(rootWindow)) {
		const target = resolveChildIframeFromWindow(receiverId, childWindow, visited, depth + 1);

		// found in sub-frame
		if (target) {
			return target;
		}
	}

	// not found amongst this window & all of its sub-frames
	return undefined;
};

/**
 * Resolves a target window by querying same-origin reachable DOM trees for an iframe with a
 * specific data attribute.
 *
 * @description The bridge API is mounted on the topmost same-origin window, but the target iframe
 * may be nested inside another same-origin iframe. Starting from the topmost same-origin window and
 * walking accessible child frame documents lets the shared transport discover cross-origin frames
 * that are only present inside nested same-origin containers.
 *
 * @param receiverId - The receiver ID to search for
 * @returns The iframe's contentWindow if found, otherwise undefined
 */
const resolveChildIframe = (receiverId: ClientId): HTMLIFrameElement | undefined => {
	const frame = findIframe(window?.document, receiverId);

	if (!frame || !frame.contentWindow) {
		return undefined;
	}

	return resolveChildIframeFromWindow(
		receiverId,
		findTopmostSameOriginWindow(window),
		new WeakSet<Window>(),
		0,
	);
};

export const resolveChildIframeTarget = (receiverId: ClientId): Window | undefined =>
	resolveChildIframe(receiverId)?.contentWindow ?? undefined;

/**
 * Resolves a target window by checking if communication should go to window.top.
 * This is used when the current window is inside an iframe and needs to communicate with the parent.
 *
 * @returns window.top if available and we're not at the top level, otherwise undefined
 */
export const resolveParentTarget = (): Window | undefined => {
	if (window.top && !isTop(window)) {
		return window.top;
	}

	return undefined;
};

export type ResolvedCrossOriginTarget = {
	target: Window;
	origin?: string;
};

const getOrigin = (url: string | null | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}

	try {
		const origin = new URL(url, window.location.href).origin;
		return origin === 'null' ? undefined : origin;
	} catch {
		return undefined;
	}
};

const getTopmostAncestorOrigin = (): string | undefined => {
	try {
		const { ancestorOrigins } = window.location;
		if (ancestorOrigins.length > 0) {
			return ancestorOrigins.item(ancestorOrigins.length - 1) ?? undefined;
		}
	} catch {}

	const referrerOrigin = getOrigin(window.document.referrer);
	return referrerOrigin === window.location.origin ? undefined : referrerOrigin;
};

/**
 * Finds a target window for cross-origin communication together with its expected origin.
 * Tries a matching child iframe first, then window.top when running inside an iframe.
 */
export const findCrossOriginTarget = (
	receiverId: ClientId,
): ResolvedCrossOriginTarget | undefined => {
	const frame = resolveChildIframe(receiverId);
	if (frame?.contentWindow) {
		return { target: frame.contentWindow, origin: getOrigin(frame.src) };
	}

	const parent = resolveParentTarget();
	return parent ? { target: parent, origin: getTopmostAncestorOrigin() } : undefined;
};
